import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthUser } from 'src/app/model/auth-user';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly accessTokenKey = 'accessToken';
  private readonly refreshTokenKey = 'refreshToken';
  private readonly currentUserSubject = new BehaviorSubject<AuthUser | null>(
    null,
  );
  readonly currentUser$ = this.currentUserSubject.asObservable();
  private readonly rawHttp: HttpClient;

  constructor(
    private readonly http: HttpClient,
    httpBackend: HttpBackend,
  ) {
    this.rawHttp = new HttpClient(httpBackend);
    this.rehydrateSession();
  }

  login(email: string, password: string): Observable<AuthUser> {
    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}/auth/login`,
        { email, password },
        { responseType: 'json' },
      )
      .pipe(
        switchMap((response) => this.validateLoginResponse(response)),
        tap((response) => {
          this.persistTokens(response.accessToken, response.refreshToken);
        }),
        switchMap(() => this.fetchCurrentUser(this.http)),
        tap((user) => this.currentUserSubject.next(user)),
        catchError((error) => throwError(() => error)),
      );
  }

  register(
    email: string,
    name: string,
    password: string,
    phone: string,
  ): Observable<AuthUser> {
    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}/auth/register`,
        { email, password, name, phone },
        { responseType: 'json' },
      )
      .pipe(
        switchMap((response) => this.validateLoginResponse(response)),
        tap((response) => {
          this.persistTokens(response.accessToken, response.refreshToken);
        }),
        switchMap(() => this.fetchCurrentUser(this.http)),
        switchMap((user) => {
          const trimmedPhone = phone.trim();
          return trimmedPhone
            ? this.updateProfile(name, trimmedPhone)
            : of(user);
        }),
        tap((user) => this.currentUserSubject.next(user)),
        catchError((error) => throwError(() => error)),
      );
  }

  forgotPassword(email: string): Observable<void> {
    return this.http
      .post(
        `${environment.apiUrl}/auth/forgot-password`,
        { email },
        { responseType: 'text' },
      )
      .pipe(
        map(() => void 0),
        catchError((error) => throwError(() => error)),
      );
  }

  resetPassword(email: string, newPassword: string): Observable<void> {
    return this.http
      .post(
        `${environment.apiUrl}/auth/reset-password`,
        { email, password: newPassword },
        { responseType: 'text' },
      )
      .pipe(
        map(() => void 0),
        catchError((error) => throwError(() => error)),
      );
  }

  verifyOtp(email: string, otp: string): Observable<void> {
    return this.http
      .post(
        `${environment.apiUrl}/auth/verify-otp`,
        { email, otp },
        { responseType: 'text' },
      )
      .pipe(
        map(() => void 0),
        catchError((error) => throwError(() => error)),
      );
  }

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.clearSession();
      return of(void 0);
    }

    return this.http
      .post(
        `${environment.apiUrl}/auth/logout`,
        { refreshToken },
        { responseType: 'text' },
      )
      .pipe(
        catchError(() => of(null)),
        tap(() => this.clearSession()),
        map(() => void 0),
      );
  }

  deleteAccount(): Observable<void> {
    return this.fetchCurrentUser(this.http).pipe(
      switchMap((user) =>
        this.http
          .delete(`${environment.apiUrl}/auth/me`, {
            headers: this.buildAuthHeaders(),
          })
          .pipe(
            tap(() => this.clearSession()),
            map(() => void 0),
          ),
      ),
      catchError((error) => {
        this.clearSession();
        return throwError(() => error);
      }),
    );
  }

  updateProfile(name: string, phone: string | null): Observable<AuthUser> {
    return this.http
      .patch<AuthUser>(
        `${environment.apiUrl}/auth/me`,
        { name, phone },
        { headers: this.buildAuthHeaders() },
      )
      .pipe(tap((user) => this.currentUserSubject.next(user)));
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Observable<void> {
    return this.http
      .patch(
        `${environment.apiUrl}/auth/me/password`,
        { currentPassword, newPassword },
        {
          headers: this.buildAuthHeaders(),
          responseType: 'text' as const,
        },
      )
      .pipe(map(() => void 0));
  }
  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  private rehydrateSession(): void {
    if (!this.getAccessToken()) {
      return;
    }

    this.fetchCurrentUser(this.rawHttp).subscribe({
      next: (user) => this.currentUserSubject.next(user),
      error: () => this.clearSession(),
    });
  }

  private fetchCurrentUser(client: HttpClient): Observable<AuthUser> {
    return client
      .get<AuthUser>(`${environment.apiUrl}/auth/me`, {
        headers: this.buildAuthHeaders(),
      })
      .pipe(
        catchError((error) => {
          if (error?.status === 401) {
            return throwError(() => error);
          }
          return throwError(() => error);
        }),
      );
  }

  private validateLoginResponse(
    response: LoginResponse,
  ): Observable<LoginResponse> {
    if (
      !response ||
      typeof response !== 'object' ||
      !('accessToken' in response)
    ) {
      return throwError(
        () =>
          new Error(
            'Authentication failed',
          ),
      );
    }
    return of(response);
  }

  private buildAuthHeaders(): HttpHeaders {
    const accessToken = this.getAccessToken();
    return new HttpHeaders({
      Authorization: `Bearer ${accessToken ?? ''}`,
    });
  }

  private persistTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  private clearSession(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    this.currentUserSubject.next(null);
  }
}
